import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import type { ButtonVariant } from 'components/partials/button/button';

import CohortCampus from 'containers/admin/clients/client/tabs-components/critical-populations/components/cohort-campus';
import DecisionPanel from 'containers/admin/clients/client/tabs-components/critical-populations/components/decision-panel';
import DeferredReasonBox from 'containers/admin/clients/client/tabs-components/critical-populations/components/deferred-reason-box';
import OriginBadge from 'containers/admin/clients/client/tabs-components/critical-populations/components/origin-badge';
import {
  CARD_ACTIONS_BY_TAB,
  CARD_ACTION_LABELS,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import {
  canDeleteRow,
  formatDimensionSubtitle,
  formatDisplayDate,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.helpers';
import type {
  CaptureListTab,
  DecisionActionKey,
  DeleteTarget,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';

import type { CriticalPopulationResponse } from 'api/generated/schemas';

import Icons from 'assets/icons';

type CardAction = (typeof CARD_ACTIONS_BY_TAB)[CaptureListTab][number];

interface ActionPresentation {
  variant: ButtonVariant;
  icon: ReactNode;
}

const ACTION_PRESENTATION: Record<CardAction, ActionPresentation> = {
  edit: {
    variant: 'brand-outlined',
    icon: <Icons.EditPencil className="size-4" aria-hidden="true" />,
  },
  'promote-evaluating': {
    variant: 'brand-filled',
    icon: <Icons.CheckCircle className="size-4" aria-hidden="true" />,
  },
  'defer-evaluating': {
    variant: 'neutral-outlined',
    icon: <Icons.Clock className="size-4" aria-hidden="true" />,
  },
  'defer-active': {
    variant: 'neutral-outlined',
    icon: <Icons.Clock className="size-4" aria-hidden="true" />,
  },
  'promote-deferred': {
    variant: 'brand-filled',
    icon: <Icons.CheckCircle className="size-4" aria-hidden="true" />,
  },
};

interface CaptureCardProps {
  row: CriticalPopulationResponse;
  tab: CaptureListTab;
  clientId: number;
  openAction: DecisionActionKey | null;
  onEdit: (id: number) => void;
  onDelete: (target: DeleteTarget) => void;
  onOpenDecision: (id: number, action: DecisionActionKey) => void;
  onCloseDecision: () => void;
  onDecisionDone: () => void;
}

const CaptureCard = ({
  row,
  tab,
  clientId,
  openAction,
  onEdit,
  onDelete,
  onOpenDecision,
  onCloseDecision,
  onDecisionDone,
}: CaptureCardProps) => {
  const { id } = row;
  const actionRefs = useRef<Partial<Record<string, HTMLButtonElement | null>>>(
    {}
  );
  const actionToRestore = useRef<string | null>(null);

  useEffect(() => {
    if (!openAction && actionToRestore.current) {
      actionRefs.current[actionToRestore.current]?.focus();
      actionToRestore.current = null;
    }
  }, [openAction]);

  if (id === undefined) return null;

  const handleCancel = (): void => {
    actionToRestore.current = openAction;
    onCloseDecision();
  };

  const handleAction = (action: CardAction): void => {
    if (action === 'edit') onEdit(id);
    else onOpenDecision(id, action);
  };

  const isRecommended = row.origin === 'RECOMMENDED';
  const hasMeta = tab !== 'deferred' || !!row.cohort || !!row.campus;

  return (
    <li
      className="overflow-hidden rounded-[10px] border border-navy-100 bg-white"
      data-testid={`capture-card-${id}`}
    >
      <div className="flex flex-col gap-2 px-4 pb-3 pt-4">
        <div className="flex">
          <OriginBadge origin={row.origin} />
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-gray-700">
            {row.summary}
          </h3>
          <p className="text-xs font-medium text-gray-500">
            {formatDimensionSubtitle(row.dimensions)}
          </p>
        </div>
        {hasMeta && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1 text-xs font-medium text-gray-500">
            <CohortCampus cohort={row.cohort} campus={row.campus} />
            {tab !== 'deferred' &&
              (isRecommended ? (
                <span>Generated: {formatDisplayDate(row.createdDate)}</span>
              ) : (
                <>
                  <span>Added by {row.createdBy}</span>
                  <span>Updated {formatDisplayDate(row.lastModifiedDate)}</span>
                </>
              ))}
          </div>
        )}
        {tab === 'deferred' && row.note && (
          <DeferredReasonBox
            note={row.note}
            deferredDate={row.lastModifiedDate}
          />
        )}
      </div>
      {openAction ? (
        <DecisionPanel
          clientId={clientId}
          id={id}
          action={openAction}
          onCancel={handleCancel}
          onDone={onDecisionDone}
        />
      ) : (
        <div className="flex items-center gap-3 border-t border-navy-100 px-4 pb-4 pt-3">
          {CARD_ACTIONS_BY_TAB[tab].map((action) => (
            <Button
              key={action}
              ref={(node) => {
                actionRefs.current[action] = node;
              }}
              variant={ACTION_PRESENTATION[action].variant}
              size="sm"
              iconBefore={ACTION_PRESENTATION[action].icon}
              onClick={() => handleAction(action)}
              data-testid={`capture-card-${id}-${action}`}
            >
              {CARD_ACTION_LABELS[action]}
            </Button>
          ))}
          {canDeleteRow(row, tab) && (
            <IconButton
              variant="critical-outlined"
              size="sm"
              icon={<Icons.Delete aria-hidden="true" />}
              aria-label={`Delete ${row.summary}`}
              className="ml-auto"
              onClick={() => onDelete({ id, summary: row.summary ?? '' })}
              data-testid={`capture-card-${id}-delete`}
            />
          )}
        </div>
      )}
    </li>
  );
};

export default CaptureCard;
